import React, { useState } from 'react';
import { Eye, Edit, MoreVertical, Download, Search } from 'lucide-react';
import UserDetailModal from './UserDetailModal';
import { User } from '@/services/PagesServices/UsersService';
import './UserDirectoryTab.css';

interface UserDirectoryTabProps {
  users: User[];
}

const UserDirectoryTab: React.FC<UserDirectoryTabProps> = ({ users }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All Department');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Sample data for modal
  const sampleCourses = [
    { id: '1', name: 'Advanced React Patterns', duration: '8 hours', lessons: 12 },
    { id: '2', name: 'TypeScript Fundamentals', duration: '6 hours', lessons: 10 },
    { id: '3', name: 'UI/UX Design Principles', duration: '10 hours', lessons: 15 },
  ];

  const sampleLearningHistory = [
    {
      id: '1',
      name: 'JavaScript Fundamentals',
      type: 'Course',
      status: 'Completed',
      progress: 100,
      score: '95%',
      enrolled: '2025-01-15',
      completed: '2025-02-20',
      certificate: true
    },
    {
      id: '2',
      name: 'React Basics',
      type: 'Course',
      status: 'In Progress',
      progress: 65,
      score: '-',
      enrolled: '2025-02-25',
      completed: '-',
      certificate: false
    },
  ];

  const sampleAdminActivities = [
    { action: 'User enrolled in Advanced React Patterns', date: '2025-11-20' },
    { action: 'Status changed to Active', date: '2025-11-15' },
    { action: 'Password reset link sent', date: '2025-11-10' },
  ];

  const handleViewUser = (user: User) => {
    // Add default values for modal display
    const userWithDefaults = {
      ...user,
      email: user.email || `${user.name.toLowerCase().replace(' ', '.')}@company.com`,
      phone: user.phone || '+1 (555) 123-4567',
      location: user.location || 'New York, USA',
      manager: user.manager || 'John Manager',
      joinDate: user.joinDate || '2024-01-15',
      progress: user.progress || 75,
    };
    setSelectedUser(userWithDefaults);
  };

  const handleEnrollCourse = (courseName: string) => {
    console.log(`Enrolling user in: ${courseName}`);
    // Add enrollment logic here
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All Department' || user.department === selectedDepartment;
    const matchesStatus = selectedStatus === 'All Status' || user.status === selectedStatus;
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="admin-user-directory">
      <div className="admin-user-directory-controls">
        <div className="admin-user-directory-search">
          <Search className="admin-user-directory-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="admin-user-directory-select"
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
        >
          <option>All Department</option>
          <option>Engineering</option>
          <option>Sales</option>
          <option>HR</option>
          <option>Design</option>
        </select>
        <select
          className="admin-user-directory-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
        <button className="admin-user-directory-export-btn">
          <Download size={18} />
          Export
        </button>
      </div>

      <div className="admin-user-directory-table-container">
        <table className="admin-user-directory-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Department</th>
              <th>Job Title</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Course</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="admin-user-directory-user-cell">
                    <div className="admin-user-directory-avatar">{user.initials}</div>
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>{user.department}</td>
                <td>{user.jobTitle}</td>
                <td>
                  <span className={`admin-user-directory-status-badge ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>{user.lastLogin}</td>
                <td>
                  <div className="admin-user-directory-course-info">
                    <span className="admin-user-directory-course-completed">{user.coursesCompleted} completed</span>
                    <span className="admin-user-directory-course-active">{user.coursesActive} active</span>
                  </div>
                </td>
                <td>
                  <div className="admin-user-directory-actions">
                    <button
                      className="admin-user-directory-action-btn"
                      onClick={() => handleViewUser(user)}
                    >
                      <Eye size={18} />
                    </button>
                    <button className="admin-user-directory-action-btn">
                      <Edit size={18} />
                    </button>
                    <button className="admin-user-directory-action-btn">
                      <MoreVertical size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="admin-user-directory-pagination">
          <div className="admin-user-directory-pagination-info">
            <span>Showing</span>
            <select
              className="admin-user-directory-pagination-select"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>of {filteredUsers.length}</span>
          </div>

          <div className="admin-user-directory-pagination-buttons">
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`admin-user-directory-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {selectedUser && (
        <UserDetailModal
          user={{
            id: selectedUser.id,
            name: selectedUser.name,
            email: selectedUser.email || '',
            jobTitle: selectedUser.jobTitle,
            department: selectedUser.department,
            initials: selectedUser.initials,
            progress: selectedUser.progress || 0,
            phone: selectedUser.phone,
            location: selectedUser.location,
            manager: selectedUser.manager,
            joinDate: selectedUser.joinDate || '',
            lastLogin: selectedUser.lastLogin,
            completedCourses: selectedUser.coursesCompleted,
            inProgressCourses: selectedUser.coursesActive,
          }}
          courses={sampleCourses}
          learningHistory={sampleLearningHistory}
          adminActivities={sampleAdminActivities}
          onClose={() => setSelectedUser(null)}
          onEnrollCourse={handleEnrollCourse}
        />
      )}
    </div>
  );
};

export default UserDirectoryTab;
